import Dialog from '@pelckmans/elementis/components/dialog';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalContext from '../../../../contexts/ModalContext';
import { fetchSmartschoolMembers } from '../../api/MyGroupsApi';
import useThrowAsyncError from '../../../../hooks/useThrowAsyncError';
import { SchoolYear } from '../../context/MyGroupsContext';
import Tabs from '@pelckmans/elementis/components/tabs';
import Badge from '@pelckmans/elementis/components/badge';
import InfoContent from './components/InfoContent';
import InfoFooter from './components/InfoFooter';
import StudentsContent from './components/StudentsContent';
import StudentsFooter from './components/StudentsFooter';
import { fetchMembers, Group, Member } from '../../../../api/groups';
import useGroups from '../../hooks/useGroups';
import { FormProvider, useForm } from 'react-hook-form';
import UnconfirmedChangesDialog from '../../../../shared-components/modals/UnconfirmedChangesDialog';

const FORM_ID = 'group-details-form';

const TABS = {
  INFO: 'INFO',
  STUDENTS: 'STUDENTS',
} as const;

type TabType = typeof TABS[keyof typeof TABS];

type Props = {
  id: string;
  groupId: string;
  schoolYear: SchoolYear;
  editable: boolean;
  onGroupUpdate: (updatedGroup: Group) => void;
  onGroupDelete: (deletedGroup: Group) => void;
};

interface FormValues {
  groupName: string;
  subjects: { id: string; name: string }[];
}

function GroupDetailsModal({ id, groupId, schoolYear, editable, onGroupUpdate, onGroupDelete }: Props) {
  const { t } = useTranslation();
  const handleError = useThrowAsyncError();
  const [selectedTab, setSelectedTab] = useState<TabType>(TABS.INFO);
  const { showModal } = useContext(ModalContext);
  const { groups } = useGroups();
  const { closeModal } = useContext(ModalContext);

  const group = groups ? groups.find(g => g.id === groupId) : undefined;

  const defaultValues = useMemo(
    () => ({
      groupName: group?.name ?? '',
      subjects: group?.subjects?.map(s => ({ name: s, id: '' })) ?? [],
    }),
    [group?.name, group?.subjects],
  );

  const methods = useForm<FormValues>({ defaultValues });
  const isDirty = Object.keys(methods.formState.dirtyFields).length > 0;

  const handleClose = () => {
    if (isDirty) {
      showModal(<UnconfirmedChangesDialog id="unconfirmed-changes" onConfirm={() => closeModal(id)} />);
    } else {
      closeModal(id);
    }
  };

  const handleTabChange = useCallback(
    (tab: TabType) => {
      if (selectedTab === tab) return;

      if (isDirty) {
        showModal(
          <UnconfirmedChangesDialog
            id="unconfirmed-changes-dialog"
            onConfirm={() => {
              setSelectedTab(tab);
              methods.reset(defaultValues, { keepDirty: false });
            }}
          />,
        );
      } else {
        setSelectedTab(tab);
      }
    },
    [selectedTab, showModal, methods, defaultValues, isDirty],
  );
  const [members, setMembers] = useState<Member[]>([]);

  useEffect(() => {
    if (!group) return;

    const getMembers = async () => {
      const isSmartschoolGroup = Boolean(group.smartschoolGroupId);

      const groupMembers = isSmartschoolGroup
        ? await fetchSmartschoolMembers(schoolYear, group.id)
        : await fetchMembers(schoolYear, group.id);

      setMembers(groupMembers);
    };

    getMembers().catch(handleError);
  }, [schoolYear, group?.id, handleError, group?.school?.isSmartschool, group]);

  if (!group) return null;

  return (
    <Dialog
      id={id}
      modal
      size="sm"
      onClose={handleClose}
      aria-label={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.A11Y_LABEL')}
    >
      <Tabs onTabChange={tab => handleTabChange(tab as TabType)} selectedTab={selectedTab}>
        <Dialog.Header
          title={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.HEADER.TITLE', { groupName: group.name })}
          onClose={handleClose}
          aria-label={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.HEADER.ACTIONS.CLOSE')}
        >
          <Tabs.TabList aria-label="tab-list" fluid>
            <Tabs.Tab id={TABS.INFO}>{t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.INFO.TITLE')}</Tabs.Tab>
            <Tabs.Tab
              id={TABS.STUDENTS}
              badge={<Badge>{group.totalAccepted + group.totalPending + group.totalDeclined}</Badge>}
            >
              {t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.TITLE')}
            </Tabs.Tab>
          </Tabs.TabList>
        </Dialog.Header>
        <Dialog.Body>
          <FormProvider {...methods}>
            <Tabs.TabsContent>
              <Tabs.TabPanel id={TABS.INFO}>
                <InfoContent
                  group={group}
                  editable={editable}
                  schoolYear={schoolYear}
                  onGroupUpdate={onGroupUpdate}
                  formId={FORM_ID}
                  onGroupDelete={onGroupDelete}
                  onClose={handleClose}
                />
              </Tabs.TabPanel>
              <Tabs.TabPanel id={TABS.STUDENTS}>
                <StudentsContent
                  group={group}
                  members={members}
                  schoolYear={schoolYear}
                  setMembers={(members: Member[]) => setMembers(members)}
                />
              </Tabs.TabPanel>
            </Tabs.TabsContent>
          </FormProvider>
        </Dialog.Body>
        {selectedTab === 'INFO' && <InfoFooter editable={editable} onClose={handleClose} formId={FORM_ID} />}
        {selectedTab === 'STUDENTS' && <StudentsFooter onClose={handleClose} />}
      </Tabs>
    </Dialog>
  );
}

export default GroupDetailsModal;
